import { useState, type FormEvent } from "react";
import * as api from "../api/client";
import { ApiError } from "../api/client";

const MAX_MESSAGE_LENGTH = 4096;

export function CommandBar({ serverId, disabled }: { serverId: string; disabled?: boolean }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSentId, setLastSentId] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSending(true);
    setError(null);
    setLastSentId(null);
    try {
      const result = await api.sendMessage(serverId, message);
      setLastSentId(result.messageId);
      setMessage("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send the message.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-header">
        <h2>Command Bar</h2>
      </div>
      <p className="muted" style={{ marginBottom: "var(--space-4)", fontSize: "13px" }}>
        Sends a plain-text message straight to this server. Cloudflare does not read, parse, or act on
        it — the message is delivered opaquely and it's entirely up to that server's own Roblox code
        (via the generated client's <code className="mono">ServerManagementMessage</code> event) to decide
        what, if anything, to do with it.
      </p>

      {disabled && (
        <div className="form-error">
          This server's authentication is invalidated. Re-authenticate or rotate its credential before
          sending messages.
        </div>
      )}
      {error && <div className="form-error">{error}</div>}
      {lastSentId && !error && (
        <div className="credential-reveal" style={{ marginBottom: "var(--space-4)" }}>
          Queued. It will reach the server on its next status poll (delivery id{" "}
          <code className="mono">{lastSentId}</code>) — check Message Logs below for delivery status.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="command-bar-message">Message</label>
          <textarea
            id="command-bar-message"
            className="input"
            rows={3}
            style={{ resize: "vertical", fontFamily: "var(--font-mono)" }}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={MAX_MESSAGE_LENGTH}
            placeholder="e.g. maintenance window starting in 10 minutes"
            disabled={disabled || sending}
            required
          />
          <p className="form-hint">
            {message.length}/{MAX_MESSAGE_LENGTH} characters. Rate-limited to 30 messages/minute per
            server.
          </p>
        </div>
        <button type="submit" className="btn btn-primary" disabled={disabled || sending || message.trim().length === 0}>
          {sending ? "Sending…" : "Send message"}
        </button>
      </form>
    </div>
  );
}
